/**
 * Build-time and default configuration
 *
 * Defaults, feature flags, the geocode endpoint, and the schema version stamped
 * into every saved record so old saves can be migrated rather than discarded.
 *
 * ◆ One module so that "what does this site do when the user has expressed no
 * preference" has a single answer, greppable and testable. A default scattered
 * across five call sites becomes five different defaults.
 *
 * © Manish Jagdish Thatte
 */

import { DEFAULT_AYANAMSA } from './astro/ayanamsa.js';

/**
 * ⚠ Bumped whenever the shape of anything persisted changes.
 *
 * `schemaVersion` on a saved record is what lets a five-year-old chart be
 * migrated instead of silently misread. A chart saved under different defaults
 * must reopen exactly as it was saved, which is why the settings travel *inside*
 * each record rather than being read from here at load time.
 */
export const SCHEMA_VERSION = 1;

/** The name every persistence key is prefixed with. */
export const STORAGE_PREFIX = 'shastra';

export const SETTINGS_KEY = `${STORAGE_PREFIX}.settings.v${SCHEMA_VERSION}`;
export const DB_NAME = `${STORAGE_PREFIX}-db`;
export const DB_VERSION = SCHEMA_VERSION;

/**
 * The defaults.
 *
 * ◆ `lahiri` because that is what every Indian pañcāṅga means by "the"
 * ayanāṁśa; `wholeSign` because that is what the classical texts assume when
 * they say "the fifth from the lagna"; `mean` node because that is the node the
 * classical texts describe.
 */
export const DEFAULTS = Object.freeze({
  locale: 'en',
  /** When set, UI stays on `locale` (usually en) and we ask the browser to translate. */
  browserLang: null,
  theme: 'system',           // 'system' | 'light' | 'dark'
  numerals: 'locale',        // 'locale' | 'latin' | 'devanagari'
  ayanamsa: DEFAULT_AYANAMSA,
  houseSystem: 'wholeSign',
  nodeMode: 'mean',
  chartStyle: 'north',       // 'north' | 'south' | 'east' | 'wheel'
  showEvidence: true,
  // Nothing is fetched from the network without an explicit user action, so this
  // stays false until someone asks for an online place lookup.
  allowNetworkGeocode: false
});

export const LOCALES = Object.freeze(['en', 'hi', 'mr', 'sa']);

/** Locales written in Devanāgarī, which default to Devanāgarī numerals. */
export const DEVANAGARI_LOCALES = Object.freeze(['hi', 'mr', 'sa']);

export const CHART_STYLES = Object.freeze(['north', 'south', 'east', 'wheel']);
export const THEMES = Object.freeze(['system', 'light', 'dark']);

/**
 * Where the city chunks live, and which one loads first.
 *
 * ⚠ The city table is **eight chunks**, not one file — see tools/build-cities.py
 * for why. `in` loads first because it is the primary audience and gives a
 * complete Indian gazetteer in 109 KB; the others load only when a search leaves
 * India.
 */
export const CITIES_INDEX = 'index';
export const CITIES_DEFAULT_CHUNK = 'in';

/**
 * The Worker's Nominatim proxy, used **only** on an explicit user action and
 * only after the UI has said a network call is about to happen.
 *
 * ◆ The whole site otherwise computes in the browser and sends nothing. That
 * property is worth more than convenience, so this is opt-in per search rather
 * than a silent fallback when the bundled table misses.
 */
export const GEOCODE_ENDPOINT = '/api/geocode';

/** Feature flags for work that lands in later phases. */
export const FEATURES = Object.freeze({
  panchanga: true,     // Phase 3
  vargas: true,        // Phase 4
  refinements: true,   // Phase 5
  strength: true,      // Phase 6
  dashas: true,        // Phase 7
  hasta: true,         // Phase 15 — reference, tracing, and automatic reading of a photograph
  learn: true,         // Phase 17
  transit: true,       // Phase 9  — gochara
  matching: true,      // Phase 12 — aṣṭakūṭa
  rectify: true,       // Phase 13
  yogas: true,         // Phase 8
  tajaka: true,        // Phase 10 — annual chart
  longevity: true,     // Phase 11 — āyurdāya, māraka, health
  remedies: true,      // Phase 11 — upāya
  mundane: true,       // Phase 14
  reading: true,       // Phase 16 — the interpretation engine's reading page
  correlation: true,   // Phase 16 — chart and palm side by side
  sources: true,       // Phase 17 — what each part of the site cites
  readings: true       // Phase 20 — paid annotated readings + console
});

/**
 * The standing disclaimer, shown on every page.
 *
 * ⚠ Not a modal, not a gate, not an age wall. 00-INDEX.md § Truth-telling is
 * explicit that nothing is hidden from the user and no interstitial stands
 * between them and a computed result. This is a visible, permanent note about
 * what the site is — which is a different thing from a warning about what it
 * might say.
 */
export const DISCLAIMER_KEY = 'disclaimer.standing';
